import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import DataTable from './data_table/DataTable';
import { sortByNumber, sortByText } from './data_table/sorting';
import { optimizedCellClassName } from './data_table/tableClasses';
import { negativeCell } from '../styles/tailwindClasses';
import RiskWorkbookExportButton from './risk_overview/RiskWorkbookExportButton';
import CompactPanelHeader from './commonComponents/CompactPanelHeader';
import { compactIconButtonSx } from './pos_sizing_table/tableStyles';
import Skeleton from './ui/Skeleton';

function formatPct(value) {
    return value !== undefined && value !== null && !Number.isNaN(Number(value))
        ? `${Number(value).toFixed(2)}%`
        : '-';
}

function formatInteger(value) {
    return value == null || Number.isNaN(Number(value))
        ? '-'
        : Math.round(Number(value)).toLocaleString();
}

// A metric value in its row's format; negative values are red.
function renderMetricValue({ row, getValue }) {
    const value = getValue();
    const text = row.original.type === 'percentage' ? formatPct(value) : formatInteger(value);
    return value != null && Number(value) < 0 ? <span className={negativeCell}>{text}</span> : text;
}

function SummaryTable({
    presentation,
    directExportEnabled,
    headerActions = null,
    isLoading = false,
    optimizedLoading = false,
}) {
    const { rows, exportRows, selectedLabel } = presentation;
    const hasData = rows.some((row) => row.actual != null || row.selected != null);
    const isInitialLoading = isLoading && !hasData;
    const exportSheets = useMemo(() => ([{
        name: 'Summary',
        kind: 'json',
        rows: exportRows,
    }]), [exportRows]);

    const selectedHeader = selectedLabel === 'Selected Portfolio' ? 'Selected' : selectedLabel;
    const columns = useMemo(() => ([
        {
            id: 'label',
            header: 'Summary',
            accessorFn: (row) => row.label,
            minSize: 115,
            sortFn: sortByText,
            meta: { grow: true },
        },
        {
            id: 'actual',
            header: 'Actual',
            accessorFn: (row) => row.actual,
            size: 95,
            sortFn: sortByNumber,
            cell: (context) => (isLoading
                ? <Skeleton width="70%" className="ml-auto" /> : renderMetricValue(context)),
            meta: { align: 'right' },
        },
        {
            id: 'selected',
            header: selectedHeader,
            accessorFn: (row) => row.selected,
            size: 100,
            sortFn: sortByNumber,
            cell: (context) => (optimizedLoading
                ? <Skeleton width="70%" className="ml-auto" /> : renderMetricValue(context)),
            meta: { align: 'right', cellClassName: optimizedCellClassName },
        },
    ]), [selectedHeader, isLoading, optimizedLoading]);

    return (
        <Box
            aria-busy={isLoading || optimizedLoading || undefined}
            aria-label={isInitialLoading ? 'Loading summary' : 'Portfolio summary'}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
            }}
        >
            <CompactPanelHeader title="Summary">
                <RiskWorkbookExportButton
                    tooltip="Download summary"
                    ariaLabel="Download summary"
                    fileName="summary.xlsx"
                    sheets={exportSheets}
                    disabled={!directExportEnabled || isInitialLoading || exportRows.length === 0}
                    sx={compactIconButtonSx}
                />
                {headerActions}
            </CompactPanelHeader>
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataTable
                    aria-label="Summary"
                    data={rows}
                    columns={columns}
                    className="h-full"
                    rowHeight={32}
                    loading={isInitialLoading}
                    loadingRowCount={rows.length || undefined}
                />
            </Box>
        </Box>
    );
}

SummaryTable.propTypes = {
    presentation: PropTypes.shape({
        rows: PropTypes.array.isRequired,
        exportRows: PropTypes.array.isRequired,
        selectedLabel: PropTypes.string.isRequired,
    }).isRequired,
    directExportEnabled: PropTypes.bool.isRequired,
    headerActions: PropTypes.node,
    isLoading: PropTypes.bool,
    optimizedLoading: PropTypes.bool,
};

export default SummaryTable;
